import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, Plus, Search, AlertCircle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Pagination } from '@/shared/components/custom/pagination/Pagination';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useUIStore } from '@/core/stores/ui.store';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useServerPagination } from '@/shared/hooks/useServerPagination';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useToast } from '@/shared/hooks/useToast';
import { useTags } from '../hooks/useTags';
import { useDeleteTag } from '../hooks/useTagMutations';
import { TagChip } from './TagChip';
import { TagFormDialog } from './TagFormDialog';
import { TagsSectionSkeleton } from './TagsSectionSkeleton';
import type { Tag } from '../types';

export function TagsSection() {
  const { t } = useTranslation('tags');
  const { toast } = useToast();
  const selectedProjectId = useUIStore((s) => s.selectedProjectId);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | undefined>(undefined);

  const { confirm, isOpen: confirmOpen, options, handleConfirm, handleCancel } = useConfirm();
  const deleteTag = useDeleteTag(selectedProjectId ?? '');

  // Initialized with 0 — totalItems/totalPages/canNext are overridden below once the
  // query resolves. Avoids a circular dependency between pagination state and query params.
  const pagination = useServerPagination({ totalItems: 0 });

  const { data, isLoading, isError } = useTags(selectedProjectId, {
    page: pagination.apiParams.page,
    pageSize: pagination.apiParams.pageSize,
    searchTerm: debouncedSearch || undefined,
  });

  const totalItems = data?.totalCount ?? 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pagination.pageSize) : 0;
  const paginationProps = {
    ...pagination,
    totalItems,
    totalPages,
    canPrevious: pagination.pageIndex > 0,
    canNext: totalPages > 0 && pagination.pageIndex < totalPages - 1,
  };

  const handleCreate = () => {
    setEditingTag(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setDialogOpen(true);
  };

  const handleDelete = async (tag: Tag) => {
    if (!tag.id) return;

    const result = await confirm({
      title: t('confirm.deleteTitle'),
      description: t('confirm.deleteDescription'),
      confirmLabel: t('confirm.confirm'),
      cancelLabel: t('confirm.cancel'),
      variant: 'destructive',
    });

    if (!result.confirmed) return;

    try {
      await deleteTag.mutateAsync(tag.id);
      toast.success({ title: t('toast.deleteSuccess') });
    } catch {
      // global handler shows error toast
    }
  };

  /* ── Sin proyecto seleccionado ─────────────────────────────── */

  if (!selectedProjectId) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 py-16 text-center"
        data-testid="tags-no-project"
      >
        <FolderOpen className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm font-medium text-foreground">{t('section.noProject')}</p>
        <p className="text-sm text-muted-foreground">{t('section.noProjectDescription')}</p>
      </div>
    );
  }

  /* ── Render ────────────────────────────────────────────────── */

  return (
    <div className="space-y-8" data-testid="tags-section">
      <div>
        <div className="pb-6">
          <h2 className="text-lg font-semibold text-foreground">{t('section.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('section.description')}</p>
        </div>

        {/* Search + create */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('actions.search')}
              className="pl-9"
              data-testid="tags-search-input"
            />
          </div>
          <Button onClick={handleCreate} data-testid="tags-create-button">
            <Plus className="h-4 w-4 mr-2" />
            {t('actions.create')}
          </Button>
        </div>

        {/* Content */}
        {isLoading ? (
          <TagsSectionSkeleton />
        ) : isError ? (
          <div
            className="flex items-center gap-2 text-sm text-destructive py-4"
            data-testid="tags-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t('list.error')}
          </div>
        ) : !data?.items?.length ? (
          <div
            className="flex flex-col items-center justify-center gap-2 py-12 text-center"
            data-testid="tags-empty"
          >
            <p className="text-sm font-medium text-foreground">{t('list.empty')}</p>
            <p className="text-sm text-muted-foreground">{t('list.emptyDescription')}</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="tags-chip-grid">
              {data.items.map((tag) => (
                <TagChip key={tag.id} tag={tag} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>

            {totalItems > pagination.pageSize && (
              <div className="mt-4 border-t" data-testid="tags-pagination">
                <Pagination {...paginationProps} />
              </div>
            )}
          </>
        )}
      </div>

      {dialogOpen && (
        <TagFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          projectId={selectedProjectId}
          tag={editingTag}
        />
      )}

      {confirmOpen && options && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
